import { useCallback, useContext, useState } from "react";
import { notification } from "antd";
import { ViewerContext } from "@/shared/lib/pdf-viewer";
import {
  openDocument as openDocumentCore,
  resolveDocumentSource,
  type ResolveDocumentSourceParams,
} from "../../document";

export type OpenDocumentParams = ResolveDocumentSourceParams & {
  fileName: string;
};

export const useDocumentOpenAction = () => {
  const { setPDF } = useContext(ViewerContext);
  const [isOpening, setIsOpening] = useState(false);
  const [loadingFileIds, setLoadingFileIds] = useState<Record<string, boolean>>(
    {},
  );

  const runWithLoading = useCallback(
    async (action: () => Promise<void>, fileId?: string) => {
      if (fileId) {
        setLoadingFileIds((prev) => ({ ...prev, [fileId]: true }));
      }

      setIsOpening(true);

      try {
        await action();
      } catch {
        notification.error({
          message: "Ошибка при открытии",
          description: "Не удалось открыть документ",
        });
      } finally {
        if (fileId) {
          setLoadingFileIds((prev) => ({ ...prev, [fileId]: false }));
        }

        setIsOpening(false);
      }
    },
    [],
  );

  const openDocument = useCallback(
    (params: OpenDocumentParams) => {
      const source = resolveDocumentSource(params);

      if (!source) {
        return;
      }

      const fileName = params.fileName || "document.pdf";
      const trackFileId = source.kind === "s3" ? source.fileId : undefined;

      runWithLoading(
        () => openDocumentCore(source, fileName, setPDF),
        trackFileId,
      ).catch(() => undefined);
    },
    [runWithLoading, setPDF],
  );

  const isFileLoading = useCallback(
    (fileId: string) => Boolean(loadingFileIds[fileId]),
    [loadingFileIds],
  );

  return {
    openDocument,
    /** @deprecated use openDocument */
    openByUrl: (documentUrl?: string, fileName?: string) => {
      if (!documentUrl) {
        return;
      }

      openDocument({ documentUrl, fileName: fileName || "document.pdf" });
    },
    /** @deprecated use openDocument */
    openByFileId: (fileId?: string, fileName?: string) => {
      if (!fileId) {
        return;
      }

      openDocument({ fileId, fileName: fileName || "document.pdf" });
    },
    /** @deprecated use openDocument */
    openLocalFile: (localBlobUrl: string, fileName?: string) => {
      if (!localBlobUrl) {
        return;
      }

      openDocument({ blobUrl: localBlobUrl, fileName: fileName || "document.pdf" });
    },
    isLoading: isOpening,
    isOpening,
    isFileLoading,
    isDownloading: false,
  };
};
